import React, { useEffect, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  type Address,
  type UpdateProfileRequestParams,
  useUpdateProfile,
} from '@entities/index';

import {
  AddressType,
  AnalyticEvent,
  BoundaryErrorType,
  addTestAttribute,
  useStores,
} from '@shared/index';
import { FullWidthPrimaryButton } from '@shared/ui';

export interface SubmitAddressProps {
  onNextStep?: () => void;
  isSubmitDisabled: boolean;
  registrationAddress?: Address;
  actualAddress?: Address;
  onSubmit: (cb: () => void) => void;
  isFormDirty: boolean;
}

export const SubmitAddresses = observer(
  ({
    onNextStep,
    isSubmitDisabled,
    registrationAddress,
    actualAddress,
    onSubmit,
    isFormDirty,
  }: SubmitAddressProps) => {
    const { t } = useTranslation();
    const { showBoundary } = useErrorBoundary();
    const {
      MainStore: {
        agentContractStore: { setAddresses, profile },
      },
    } = useStores();

    const [requestParams, setRequestParams] =
      useState<UpdateProfileRequestParams>({
        isComplete: false,
        profile: {
          ...profile,
          addressesAgents: undefined,
        },
      });

    const {
      res,
      error,
      isFetching,
      refetch: updateAgentProfile,
    } = useUpdateProfile(
      {
        ...requestParams,
      },
      [requestParams]
    );

    useEffect(() => {
      if (!isFetching && res) {
        if (!!requestParams?.profile?.addressesAgents?.length) {
          setAddresses(requestParams.profile.addressesAgents);
          sendAnalyticEvent(AnalyticEvent.AGENT_AGREEMENT_TOSTEP_REQUISITES);
          onNextStep?.();
        }
      }
    }, [isFetching, res]);

    useEffect(() => {
      if (!!requestParams?.profile?.addressesAgents?.length) {
        updateAgentProfile();
      }
    }, [requestParams]);

    useEffect(() => {
      if (error) {
        showBoundary(BoundaryErrorType.SomethingWentWrong);
      }
    }, [error]);

    const createRequestPayload = () => {
      if (!isFormDirty) {
        sendAnalyticEvent(AnalyticEvent.AGENT_AGREEMENT_TOSTEP_REQUISITES);
        onNextStep?.();
      }

      if (registrationAddress) {
        const realAddress = actualAddress
          ? actualAddress
          : { ...registrationAddress, addressType: AddressType.ACCOMMODATION };

        setRequestParams((prevParams) => ({
          ...prevParams,
          profile: {
            ...prevParams.profile,
            addressesAgents: [registrationAddress, realAddress],
          },
        }));
      }
    };

    const submitPage = () => {
      onSubmit(createRequestPayload);
    };

    return (
      <FullWidthPrimaryButton
        size='l'
        onClick={submitPage}
        disabled={isSubmitDisabled}
        isLoading={isFetching}
        {...addTestAttribute('agentAddress.submitButton')}
      >
        {t('COMMON:buttons.continue')}
      </FullWidthPrimaryButton>
    );
  }
);
